import { supabaseClient } from '../../services/supabase';
import { fetchCarbonIntensityData, CarbonIntensityResult } from '../../services/carbonIntensityService';
import { getBuildingsEnergyData, calculateElectricityEmissions } from '../../services/buildingsEnergyDataService';
import {
  fetchCaliforniaStateEnergyActivityFromGhgInventory,
  type CaliforniaStateEnergyActivityRow
} from '../../services/californiaGhgInventoryEnergyActivityService';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { DataService } from '../../services/dataService';
import { fetchMergedEmfacLdvFactorsForInventory } from '../../services/emfacInventoryLdvFactors';
import { fetchAgricultureEmissionsByYearMt } from '../../services/agricultureEmissionsInventory';
import { fetchElectricityCarbonIntensityMapForGeofips } from '../../services/carbonIntensitySeriesCore';
import { fetchCaliforniaGhgInventoryResultFromCountyPrecalc } from 'services/californiaStateGhgInventoryFromCountyPrecalc';
import {
  CALIFORNIA_STATE_GEOFIPS,
  isCaliforniaStateInventoryLocation,
} from '../../services/californiaGhgInventoryVmtService';
import type { GHGInventoryResult } from 'components/charts/ghgInventoryChartTypes';
import { COMBINED_2015_2022_METHOD_VERSION } from '../../utils/vmtCombinedCore';
import type { GhgInventoryEvidence, InventoryYearSeries } from '../../services/ghgInventoryEvidence';
import { emptyYearSeries } from '../../services/ghgInventoryEvidence';
import { computeSectorInventoryHeadlines } from 'utils/ghgInventoryHeadlineMetrics';
import { ghgInventoryCategoryRgba } from '../../theme/navChartPalette';
import { fetchScaledWastewaterSeries } from '../../services/wastewaterInventoryService';
import { ghgElectricityNonresidentialCategoryLabel } from '../../domains/eia861UtilityElectricity/municipalUtilityElectricity';

export type { GHGInventoryResult } from 'components/charts/ghgInventoryChartTypes';

// Helper function to get geofips for a location
async function getGeofipsForLocation(location: string): Promise<string | null> {
  try {
    // Use location service instead of ccas table
    await supabaseLocationService.initialize().catch(() => {});
    const geofips = await supabaseLocationService.getGeofips(location);
    
    if (geofips) {
      return String(geofips);
    }
    
    return null; // Return null instead of defaulting to Berkeley
  } catch (error) {
    return null; // Return null instead of defaulting to Berkeley
  }
}


// LDV tons/mile: EMFAC2025 county filtered preferred, EMFAC2021 fills gaps (matches Policy Tool baseline).
async function fetchEMFACData(location: string): Promise<{ countyData: any[]; californiaData: any[] }> {
  const { countyData, californiaData } = await fetchMergedEmfacLdvFactorsForInventory(
    location,
    supabaseClient
  );
  return { countyData, californiaData };
}



async function applyVmt2022AadtBackcastIfNeeded(
  vmtData: Array<{ year: number; vmt: number; source?: string; method_note?: string }>,
  geofips?: number | null
): Promise<Array<{ year: number; vmt: number; source?: string; method_note?: string }>> {
  const vmt2022 = vmtData.find(d => d.year === 2022);
  const vmt2023 = vmtData.find(d => d.year === 2023);
  if (!geofips || (vmt2022 && vmt2022.vmt > 0) || !vmt2023 || vmt2023.vmt <= 0) {
    return vmtData;
  }

  const out = vmtData.map(d => ({ ...d }));
  try {
    const { data: aadtData, error: aadtError } = await supabaseClient
      .from('aadt_filtered')
      .select('year, value')
      .eq('geofips', geofips)
      .eq('year', 2022)
      .single();

    if (!aadtError && aadtData && aadtData.value) {
      const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtData.value);

      const vmt2022Index = out.findIndex(d => d.year === 2022);
      if (vmt2022Index >= 0) {
        out[vmt2022Index].vmt = backcastedVMT2022;
        out[vmt2022Index].source = 'vmt_chart_backcasted';
        out[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
      } else {
        out.push({
          year: 2022,
          vmt: backcastedVMT2022,
          source: 'vmt_chart_backcasted',
          method_note: 'VMT data backcasted from 2023 using AADT'
        });
        out.sort((a, b) => a.year - b.year);
      }
    }
  } catch (error) {
    // AADT backcast failed; proceed without it
  }
  return out;
}

/**
 * Fetch VMT data for GHG Inventory
 * Preferentially uses Transportation page data (location-tagged), falls back to database if not available
 * Data is cached in global state for persistence across navigation
 */
async function fetchVMTDataForGHGInventory(location: string, geofips?: number | null): Promise<any[]> {
  try {
    const { getVMTDataForGHG } = await import('../../services/vmtDataService');
    let vmtData = await getVMTDataForGHG(location, geofips || undefined);

    if (vmtData.length > 0) {
      vmtData = await applyVmt2022AadtBackcastIfNeeded(vmtData, geofips);
      return vmtData;
    }

    // FALLBACK: Fetch absolute VMT by geofips only (no location name; no county fallback for absolute values).
    
    let vmtDbData: any[] = [];
    if (geofips) {
      try {
        // LDV VMT = PRIVATE_AUTO + ON_DEMAND_AUTO (same as Transportation page); replica_vmt_by_mode has no 'total' mode
        vmtDbData = await DataService.getVMTData(undefined, ['PRIVATE_AUTO', 'ON_DEMAND_AUTO'], geofips);
      } catch (error) {
        // DataService.getVMTData failed; proceed with empty array
      }
    }
    // Aggregate by year: sum VMT across PRIVATE_AUTO and ON_DEMAND_AUTO per year
    const vmtByYear = new Map<number, number>();
    for (const d of vmtDbData) {
      const y = d.year;
      const v = Number(d.vmt) || 0;
      vmtByYear.set(y, (vmtByYear.get(y) ?? 0) + v);
    }
    const fallbackVmtSeries = Array.from(vmtByYear.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, vmt]) => ({
        year,
        vmt,
        source: 'database_direct',
        method_note: 'VMT data fetched directly from database (same source as Transportation page)'
      }));
    
    // Update global state with fetched data so it persists for subsequent calls
    if (fallbackVmtSeries.length > 0) {
      const { setVMTDataFromTransportation } = await import('../../services/vmtDataService');
      setVMTDataFromTransportation(fallbackVmtSeries, location);
      if (typeof window !== 'undefined') {
        window.vmtChartData = fallbackVmtSeries;
      }
    }
    
    return fallbackVmtSeries;
  } catch (error) {
  return [];
  }
}



// Track in-flight requests to prevent duplicate calls
const inFlightRequests = new Map<string, Promise<GHGInventoryResult>>();
let inventoryFetchStarts = 0;

export function resetGhgInventoryFetchStarts(): void {
  inventoryFetchStarts = 0;
}

export function getGhgInventoryFetchStarts(): number {
  return inventoryFetchStarts;
}

function energyActivitySeries(
  years: number[],
  block: { years: number[]; values: Array<number | null>; sources?: string[]; providers?: string[] } | undefined,
  unit: string
): InventoryYearSeries {
  const out = emptyYearSeries(years, unit);
  if (!block) return out;
  years.forEach((y, i) => {
    const idx = block.years.indexOf(y);
    if (idx < 0) return;
    const v = block.values[idx];
    out.values[i] = v == null || !Number.isFinite(Number(v)) ? null : Number(v);
    if (out.statuses) out.statuses[i] = block.sources?.[idx] ?? null;
    if (out.sources) out.sources[i] = block.providers?.[idx] ?? null;
  });
  return out;
}

export async function fetchGHGInventoryData(locationParam: string): Promise<GHGInventoryResult> {
  // Use a different variable name to avoid conflict with DOM Location type
  const location: string = locationParam;
  
  // Check if there's already an in-flight request for this location
  const existingRequest = inFlightRequests.get(location);
  if (existingRequest) {
    return existingRequest;
  }

  inventoryFetchStarts += 1;
  
  // Create a new request and store it
  const requestPromise = (async () => {
    try {
      // Resolve geofips first so California State can skip VMT wait and use county precalc sums.
      let geofips: number | null = null;
      try {
        await supabaseLocationService.initialize();
        geofips = await supabaseLocationService.getGeofips(location);
        console.log('🔍 Geofips for location:', geofips);
      } catch (error) {
        console.warn('Could not get geofips for location, will try location name:', error);
      }

      if (isCaliforniaStateInventoryLocation(location, geofips)) {
        console.log(
          'GHG Inventory: California statewide — chart = sum of CA county rows in ghg_inventory_precalculated'
        );
        return fetchCaliforniaGhgInventoryResultFromCountyPrecalc(supabaseClient, location);
      }

      // Prefer Transportation page VMT edits when available, but do not require it.
      // If unavailable, fetchVMTDataForGHGInventory() will fall back to direct database fetch.
      const { isVMTDataAvailable } = await import('../../services/vmtDataService');
      if (!isVMTDataAvailable(location)) {
        // Brief wait for LocationContext/Transportation preload; then fetchVMTDataForGHGInventory will fetch VMT directly so Policy works without visiting Transport first.
        await new Promise<void>((resolve) => {
          let attempts = 0;
          const maxAttempts = 15; // ~6s; then we fetch VMT ourselves
          const intervalMs = 400;
          const checkInterval = setInterval(() => {
            attempts++;
            if (isVMTDataAvailable(location) || attempts >= maxAttempts) {
              clearInterval(checkInterval);
              resolve();
            }
          }, intervalMs);
        });
      }
      if (isVMTDataAvailable(location)) {
        console.log('✅ GHG Inventory: VMT data available from Transportation page');
      } else {
        console.log('GHG Inventory: Using fallback VMT fetch');
      }

      // Get VMT data using original GHG Inventory method (not affected by Policy Tool)
      const vmtData = await fetchVMTDataForGHGInventory(location, geofips);
    
    // Fetch city-specific HDV total_ghg from emfac2025_fleet
    // Note: emfac2025_fleet doesn't have total_ghg, so we calculate it from total_vmt * gco2_per_mile / 1000000
    let hdvCityGHGDataByYear: Map<number, number> = new Map();
    
    // Absolute HDV values: lookup by geofips only (no location name; avoid inconsistent spelling).
    // Do not use county fallback for absolute values—only for scaling ratios or projections elsewhere.
    let hdvDataArray: any[] = [];
    if (geofips) {
      const { data: hdv2025DataByGeofips, error: hdv2025ErrorByGeofips } = await supabaseClient
        .from('emfac2025_fleet')
        .select('year, total_vmt, gco2_per_mile')
        .eq('geofips', geofips)
        .eq('class', 'HDV')
        .gte('year', 2015)
        .lte('year', 2023)
        .order('year', { ascending: true });

      if (!hdv2025ErrorByGeofips && hdv2025DataByGeofips && hdv2025DataByGeofips.length > 0) {
        hdvDataArray = hdv2025DataByGeofips.map(row => ({
          year: row.year,
          total_vmt: row.total_vmt == null ? null : Number(row.total_vmt),
          total_ghg: row.total_vmt && row.gco2_per_mile ? (row.total_vmt * row.gco2_per_mile) / 1000000 : 0
        }));
      }
    }

    // Sum total_ghg by year (in case there are multiple rows per year for same location)
    let hdvVmtByYear: Map<number, number> = new Map();
    hdvDataArray.forEach((row: any) => {
      const year = row.year;
      const totalGhg = row.total_ghg || 0;
      const currentSum = hdvCityGHGDataByYear.get(year) || 0;
      hdvCityGHGDataByYear.set(year, currentSum + totalGhg);
      const vmt = Number(row.total_vmt);
      if (Number.isFinite(vmt)) {
        hdvVmtByYear.set(year, (hdvVmtByYear.get(year) || 0) + vmt);
      }
    });
    
    // Apply data smoothing to detect and fix anomalies (same logic as VMT Comparison Chart)
    // If year-over-year change > 50%, use previous year's value instead
    const sortedYears = Array.from(hdvCityGHGDataByYear.keys()).sort((a, b) => a - b);
    const smoothedHdvByYear = new Map<number, number>();
    const hdvSmoothingAppliedByYear = new Map<number, boolean>();
    
    sortedYears.forEach((year, index) => {
      const currentValue = hdvCityGHGDataByYear.get(year) || 0;
      
      if (index === 0) {
        // First year: use as-is
        smoothedHdvByYear.set(year, currentValue);
        hdvSmoothingAppliedByYear.set(year, false);
      } else {
        const previousYear = sortedYears[index - 1];
        const previousValue = smoothedHdvByYear.get(previousYear) || 0;
        
        // Calculate year-over-year change percentage
        const changePercent = previousValue > 0 
          ? Math.abs(currentValue - previousValue) / previousValue 
          : 1;
        
        if (changePercent > 0.5) {
          // Anomaly detected: use previous year's value
          smoothedHdvByYear.set(year, previousValue);
          hdvSmoothingAppliedByYear.set(year, true);
        } else {
          // Normal change: use current value
          smoothedHdvByYear.set(year, currentValue);
          hdvSmoothingAppliedByYear.set(year, false);
        }
      }
    });
    
    // Replace original map with smoothed values
    hdvCityGHGDataByYear.clear();
    smoothedHdvByYear.forEach((value, year) => {
      hdvCityGHGDataByYear.set(year, value);
    });
    
    // Fetch county-level HDV backcasting ratios for years 2005-2014
    await supabaseLocationService.initialize().catch(() => {});
    const countyResult = supabaseLocationService.getCountyForCity(location);
    const resolvedCounty = (typeof countyResult === 'string' ? countyResult : location).trim();
    const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
    const countyVariations = Array.from(new Set([
      `${baseCountyName} County`,
      baseCountyName,
      resolvedCounty
    ]));
    
    let hdvBackcastingRatios: { year: number; backcasting_ratio: number }[] = [];
    
    // Try location_name first - use EMFAC2025
    for (const locName of countyVariations) {
      const { data, error } = await supabaseClient
        .from('emfac2025_county_filtered')
        .select('year, backcasting_ratio')
        .eq('location_name', locName)
        .eq('class', 'HDV')
        .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
        .order('year', { ascending: true });
      if (!error && data && data.length > 0) {
        hdvBackcastingRatios = data as any;
        break;
      }
    }
    
    // Fallback: Try to get county geofips and query by geofips
    if (hdvBackcastingRatios.length === 0 && geofips) {
      try {
        // Get location info to find county
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('county, geography_type')
          .eq('geofips', geofips)
          .limit(1);
        
        if (locationData && locationData.length > 0 && locationData[0].county) {
          const countyName = locationData[0].county;
          // Try to get county geofips (typically 4-digit codes like 6001)
          const { data: countyData } = await supabaseClient
            .from('population')
            .select('geofips')
            .eq('location_name', countyName)
            .eq('geography_type', 'County')
            .limit(1);
          
          if (countyData && countyData.length > 0) {
            const countyGeofips = countyData[0].geofips;
            const { data, error } = await supabaseClient
              .from('emfac2025_county_filtered')
              .select('year, backcasting_ratio')
              .eq('geofips', countyGeofips)
              .eq('class', 'HDV')
              .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
              .order('year', { ascending: true });
            if (!error && data && data.length > 0) {
              hdvBackcastingRatios = data as any;
            }
          }
        }
      } catch (e) {
        // Error trying to get county geofips for HDV backcasting
      }
    }
    
    const { countyData: emfacCountyData, californiaData: emfacCaliforniaData } = await fetchEMFACData(location);

    // Calculate light duty vehicle emissions (2005-2023 only)
    const lightDutyEmissions = [];
    
    // Calculate average emission factor from county data for reference
    const validCountyEmissionFactors = emfacCountyData.filter(d => d.ghg_per_mile > 0);
    const avgCountyEmissionFactor = validCountyEmissionFactors.length > 0 
      ? validCountyEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCountyEmissionFactors.length
      : 0;
    
    // Calculate average emission factor from California data for fallback
    const validCaliforniaEmissionFactors = emfacCaliforniaData.filter(d => d.ghg_per_mile > 0);
    const avgCaliforniaEmissionFactor = validCaliforniaEmissionFactors.length > 0 
      ? validCaliforniaEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCaliforniaEmissionFactors.length
      : 0.0008; // Final fallback emission factor (tons CO2 per mile)
    
    for (let year = 2005; year <= 2023; year++) {
      const vmt = vmtData.find(d => d.year === year)?.vmt || 0;
      
      // Priority 1: Use year-specific emission factor from county data
      let yearEmissionFactorData = emfacCountyData.find(d => d.year === year);
      let emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
      let source = 'county-year-specific';
      
      // Priority 2: If county year-specific not available, use California year-specific
      if (emissionFactor === 0) {
        yearEmissionFactorData = emfacCaliforniaData.find(d => d.year === year);
        emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
        source = 'california-year-specific';
      }
      
      // Priority 3: If no year-specific data, use county average
      if (emissionFactor === 0 && avgCountyEmissionFactor > 0) {
        emissionFactor = avgCountyEmissionFactor;
        source = 'county-average';
      }
      
      // Priority 4: If no county average, use California average
      if (emissionFactor === 0) {
        emissionFactor = avgCaliforniaEmissionFactor;
        source = 'california-average';
      }
      
      // Priority 5: Final fallback
      if (emissionFactor === 0) {
        emissionFactor = 0.0008;
        source = 'final-fallback';
      }
      
      const finalEmissionFactor = emissionFactor;
      const emissions = vmt * finalEmissionFactor;
      
      lightDutyEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Calculate heavy duty vehicle emissions
    // For 2015-2023: Use SUM of total_ghg from emfac2025_fleet (calculated from total_vmt * gco2_per_mile / 1000000)
    // For 2005-2014: Backcast using 2015 value and county-level backcasting_ratio from emfac2021_county_filtered
    const heavyDutyEmissions = [];
    
    // Get 2015 HDV emissions as baseline for backcasting
    const hdv2015 = hdvCityGHGDataByYear.get(2015) || 0;
    
    for (let year = 2005; year <= 2023; year++) {
      let hdvEmissions = 0;
      
      if (year >= 2015) {
        // Use actual data from emfac2025_fleet (calculated from total_vmt * gco2_per_mile / 1000000)
        hdvEmissions = hdvCityGHGDataByYear.get(year) || 0;
          } else {
        // Backcast from 2015 using county-level backcasting_ratio
        const backcastRatio = hdvBackcastingRatios.find(r => r.year === year);
        if (backcastRatio && backcastRatio.backcasting_ratio && hdv2015 > 0) {
          hdvEmissions = hdv2015 * backcastRatio.backcasting_ratio;
        } else {
          // If no ratio available, use 2015 value as fallback
          hdvEmissions = hdv2015;
        }
      }
      
      heavyDutyEmissions.push({
        year,
        emissions: Math.round(hdvEmissions)
      });
    }

    // Calculate natural gas emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const buildingsEnergyData = await getBuildingsEnergyData(location);

    const naturalGasEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      // Calculate residential natural gas emissions using ONLY residential therms
      // EPA's Greenhouse Gas Equivalencies Calculator: 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      const yearIndex = buildingsEnergyData.naturalGas.residential.years.indexOf(year);
      const resTherms = buildingsEnergyData.naturalGas.residential.values[yearIndex] || 0;
      const emissions = resTherms * 0.00529; // 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      
      naturalGasEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Calculate electricity emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const electricityEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const emissions = calculateElectricityEmissions(buildingsEnergyData, year);
      electricityEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Get geofips for carbon intensity lookup
    const carbonIntensityGeofips = await getGeofipsForLocation(location);

    // Fetch carbon intensity data for the location (only if geofips is available)
    let carbonIntensityData: CarbonIntensityResult | null = null;
    if (carbonIntensityGeofips) {
      try {
        carbonIntensityData = await fetchCarbonIntensityData(carbonIntensityGeofips);
      } catch (error) {
        carbonIntensityData = null;
      }
    }

    const tableCiGPerKwhByYear = new Map<number, number>();
    if (carbonIntensityGeofips) {
      const gNum = Number(carbonIntensityGeofips);
      if (Number.isFinite(gNum)) {
        const m = await fetchElectricityCarbonIntensityMapForGeofips(supabaseClient, gNum, 2005, 2023);
        m.forEach((v, y) => tableCiGPerKwhByYear.set(y, v));
      }
    }

    // Use dynamic carbon intensity or fallback to 200 g CO2/kWh
    const fallbackCarbonIntensity = 0.0002; // MT CO2 per kWh (200 g CO2/kWh)
    
    // Helper function to get carbon intensity for a specific year
    const getCarbonIntensityForYear = (year: number): number => {
      const gTable = tableCiGPerKwhByYear.get(year);
      if (gTable != null && gTable > 0) {
        const intensity = gTable / 1000000;
        return intensity;
      }
      if (carbonIntensityData && carbonIntensityData.chartData) {
        const yearIndex = carbonIntensityData.chartData.labels.indexOf(year);
        if (yearIndex !== -1 && carbonIntensityData.chartData.datasets[0]?.data[yearIndex] !== undefined) {
          // Convert from g CO2/kWh to MT CO2/kWh
          const intensity = carbonIntensityData.chartData.datasets[0].data[yearIndex] / 1000000;
          return intensity;
        }
      }
      return fallbackCarbonIntensity;
    };

    // California State (geofips 6): use ghg_inventory_precalculated State row for electricity (sum of counties)
    // State row (county sums) for electricity — same basis as Energy page / upsert_ca_state_ghg.
    const useCaStateElecInventory =
      geofips != null && Number(geofips) === CALIFORNIA_STATE_GEOFIPS;
    const caElecInventoryByYear = new Map<number, CaliforniaStateEnergyActivityRow>();
    if (useCaStateElecInventory) {
      const invRows = await fetchCaliforniaStateEnergyActivityFromGhgInventory(supabaseClient);
      for (const r of invRows) {
        caElecInventoryByYear.set(r.year, r);
      }
    }

    // Calculate electricity emissions using Buildings page data with dynamic carbon intensity
    const electricityCommercialEmissions = [];
    const electricityResidentialEmissions = [];
    
    for (let year = 2005; year <= 2023; year++) {
      const carbonIntensity = getCarbonIntensityForYear(year);

      let resKwh =
        buildingsEnergyData.electricity.residential.values[
          buildingsEnergyData.electricity.residential.years.indexOf(year)
        ] || 0;
      let comKwh =
        buildingsEnergyData.electricity.commercial.values[
          buildingsEnergyData.electricity.commercial.years.indexOf(year)
        ] || 0;

      let resEmissions: number;
      let comEmissions: number;

      const inv = useCaStateElecInventory ? caElecInventoryByYear.get(year) : undefined;
      if (inv) {
        const invKwhSum = inv.elec_residential_kwh + inv.elec_commercial_kwh;
        const hasPrecalcEmissions =
          inv.elec_residential_emissions != null && inv.elec_commercial_emissions != null;
        if (hasPrecalcEmissions) {
          resEmissions = Math.round(inv.elec_residential_emissions as number);
          comEmissions = Math.round(inv.elec_commercial_emissions as number);
        } else if (invKwhSum > 0) {
          resKwh = inv.elec_residential_kwh;
          comKwh = inv.elec_commercial_kwh;
          resEmissions = Math.round(resKwh * carbonIntensity);
          comEmissions = Math.round(comKwh * carbonIntensity);
        } else {
          resEmissions = Math.round(resKwh * carbonIntensity);
          comEmissions = Math.round(comKwh * carbonIntensity);
        }
      } else {
        resEmissions = Math.round(resKwh * carbonIntensity);
        comEmissions = Math.round(comKwh * carbonIntensity);
      }

      electricityCommercialEmissions.push({
        year,
        emissions: comEmissions
      });

      electricityResidentialEmissions.push({
        year,
        emissions: resEmissions
      });
    }

    // Calculate natural gas commercial emissions using Buildings page data
    const naturalGasCommercialEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const comTherms = buildingsEnergyData.naturalGas.commercial.values[buildingsEnergyData.naturalGas.commercial.years.indexOf(year)] || 0;
      const emissions = comTherms * 0.00529; // EPA's Greenhouse Gas Equivalencies Calculator: 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      
      naturalGasCommercialEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Fetch waste emissions using same calculation as GHGs chart
    // Calculate: waste_tons × emission_factor, where emission_factor = county-level SUM(ghg_emissions) / SUM(waste_tons)
    const wasteEmissions = [];
    // Store waste tons and emission factors for summary logging
    const wasteTonsByYearForLogging: Record<number, number> = {};
    const wasteEmissionFactorsByYearForLogging: Record<number, number> = {};
    if (geofips) {
      try {
        // Get location info to determine if it's a city or county (need county geofips for landfill data)
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('geography_type, county')
          .eq('geofips', geofips)
          .limit(1);

        let resolvedGeofips = geofips;
        let countyGeofips = geofips;

        // If it's a city, try to get county geofips for landfill emissions data
        if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
          const countyName = locationData[0].county;
          if (countyName) {
            const { data: countyData } = await supabaseClient
              .from('population')
              .select('geofips')
              .eq('location_name', countyName)
              .eq('geography_type', 'County')
              .limit(1);

            if (countyData && countyData.length > 0) {
              countyGeofips = countyData[0].geofips;
            }
          }
        }

        // Fetch waste tons from total_waste for the selected location
        const { data: wasteData, error: wasteError } = await supabaseClient
          .from('total_waste')
          .select('year, tons_waste')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        // Fetch landfill emissions data at county level to calculate emission factors
        const { data: landfillData, error: landfillError } = await supabaseClient
          .from('landfill_emissions_full')
          .select('year, waste_tons, ghg_emissions')
          .eq('geofips', countyGeofips)
          .order('year', { ascending: true });

        // Calculate county-specific emission factors by year
        // SUM(GHG Emissions) per county and year / SUM(waste_tons) per county and year
        const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
        (landfillData || []).forEach((r: any) => {
          const year = r.year;
          if (!countyTotalsByYear[year]) {
            countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
          }
          countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
          countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
        });

        // Calculate emission factor (carbon intensity) for each year
        const emissionFactorsByYear: Record<number, number> = {};
        Object.keys(countyTotalsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          const totals = countyTotalsByYear[year];
          if (totals.totalWaste > 0) {
            emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
          }
        });

        // Check if we have valid county emission factors, if not fetch statewide average by aggregating all California counties
        const hasValidCountyFactors = Object.keys(emissionFactorsByYear).length > 0 && 
          Object.values(emissionFactorsByYear).some(factor => factor > 0);
        
        if (!hasValidCountyFactors) {
          // Fetch all California county data (geofips starting with 60, typically 4-digit county codes)
          // This aggregates all counties to get California-wide averages
          const { data: californiaLandfillData, error: californiaError } = await supabaseClient
            .from('landfill_emissions_full')
            .select('year, waste_tons, ghg_emissions, geofips')
            .gte('geofips', 6000)
            .lt('geofips', 7000) // California counties are typically 6000-6999
            .order('year', { ascending: true });

          if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
            // Calculate statewide emission factors
            const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
            californiaLandfillData.forEach((r: any) => {
              const year = r.year;
              if (!stateTotalsByYear[year]) {
                stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
              }
              stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
              stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
            });

            // Calculate statewide emission factors
            Object.keys(stateTotalsByYear).forEach(yearStr => {
              const year = parseInt(yearStr);
              const totals = stateTotalsByYear[year];
              if (totals.totalWaste > 0) {
                emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
              }
            });
          }
        } else {
          // If we have some county factors but not all years, fill missing years with statewide average
          const allRequiredYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
          const missingYears = allRequiredYears.filter(year => !emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0);
          
          if (missingYears.length > 0) {
            // Fetch all California county data to calculate statewide averages
            const { data: californiaLandfillData, error: californiaError } = await supabaseClient
              .from('landfill_emissions_full')
              .select('year, waste_tons, ghg_emissions, geofips')
              .gte('geofips', 6000)
              .lt('geofips', 7000) // California counties are typically 6000-6999
              .order('year', { ascending: true });

            if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
              // Calculate statewide emission factors for missing years
              const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
              californiaLandfillData.forEach((r: any) => {
                const year = r.year;
                if (missingYears.includes(year)) {
                  if (!stateTotalsByYear[year]) {
                    stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
                  }
                  stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
                  stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
                }
              });

              // Fill in missing years with statewide factors
              Object.keys(stateTotalsByYear).forEach(yearStr => {
                const year = parseInt(yearStr);
                const totals = stateTotalsByYear[year];
                if (totals.totalWaste > 0 && (!emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0)) {
                  emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
                }
              });
            }
          }
        }

        // Project carbon intensity linearly for years 2020-2023 if data not available
        const yearsWithFactors = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        if (yearsWithFactors.length > 0) {
          const latestYearWithFactor = Math.max(...yearsWithFactors);
          const lastYear = latestYearWithFactor;
          const lastFactor = emissionFactorsByYear[lastYear];
          
          // If latest year is before 2023, project forward
          if (latestYearWithFactor < 2023) {
            // Find the last two years with data for linear projection
            const secondLastYear = yearsWithFactors[yearsWithFactors.length - 2];
            
            if (secondLastYear && secondLastYear < lastYear) {
              const secondLastFactor = emissionFactorsByYear[secondLastYear];
              
              // Calculate trend per year
              const yearsDiff = lastYear - secondLastYear;
              const factorDiff = lastFactor - secondLastFactor;
              const trendPerYear = factorDiff / yearsDiff;
              
              // Project for years 2020-2023 if missing
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  const yearsToProject = year - lastYear;
                  emissionFactorsByYear[year] = lastFactor + (trendPerYear * yearsToProject);
                }
              }
            } else {
              // If only one year of data, use that value for all missing years
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  emissionFactorsByYear[year] = lastFactor;
                }
              }
            }
          }
        }

        // Fetch population data for backcasting waste tons
        const { data: populationData } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        // Build population by year
        const populationByYear: Record<number, number> = {};
        (populationData || []).forEach((r: any) => {
          populationByYear[r.year] = Number(r.population || 0);
        });

        // Aggregate waste tons by year for the selected location
        const wasteTonsByYear: Record<number, number> = {};
        (wasteData || []).forEach((r: any) => {
          const year = r.year;
          wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
        });

        // Backcast waste tons to 2005 using population ratios
        const wasteYears = Object.keys(wasteTonsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

        if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
          const baseTons = wasteTonsByYear[earliestWasteYear];
          const basePopulation = populationByYear[earliestWasteYear];

          // Backcast years before earliest waste data year
          for (let year = 2005; year < earliestWasteYear; year++) {
            const yearPopulation = populationByYear[year] || basePopulation;
            const populationRatio = yearPopulation / basePopulation;
            
            // Backcast tons using population ratio
            wasteTonsByYear[year] = baseTons * populationRatio;
          }
        }

        // Backcast emission factors to 2005 if needed
        const factorYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestFactorYear = factorYears.length > 0 ? Math.min(...factorYears) : null;

        if (earliestFactorYear && earliestFactorYear > 2005) {
          const baseFactor = emissionFactorsByYear[earliestFactorYear];
          
          // Use earliest factor for all years before earliest data year
          for (let year = 2005; year < earliestFactorYear; year++) {
            emissionFactorsByYear[year] = baseFactor;
          }
        }

        // Calculate GHG emissions = waste_tons × emission_factor for each year (2005-2023)
        const calculatedEmissionsByYear: Record<number, number> = {};
        const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
        
        allYears.forEach(year => {
          const wasteTons = wasteTonsByYear[year] || 0;
          const emissionFactor = emissionFactorsByYear[year] || 0;
          
          if (wasteTons > 0 && emissionFactor > 0) {
            calculatedEmissionsByYear[year] = wasteTons * emissionFactor;
          } else {
            calculatedEmissionsByYear[year] = 0;
          }
        });
        
        // Store waste tons and emission factors for summary logging
        Object.keys(wasteTonsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          wasteTonsByYearForLogging[year] = wasteTonsByYear[year];
        });
        Object.keys(emissionFactorsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          wasteEmissionFactorsByYearForLogging[year] = emissionFactorsByYear[year];
        });
        
        // Create waste emissions array for all years 2005-2023, adding scaled wastewater when available
        const wasteYearsForWw = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
        let wastewaterByYear: Record<number, number> = {};
        try {
          const wastewaterSeries = await fetchScaledWastewaterSeries(Number(resolvedGeofips), wasteYearsForWw);
          wastewaterByYear = wastewaterSeries.byYear;
        } catch {
          wastewaterByYear = {};
        }
        const wasteChartLabel = 'Waste & wastewater';
        for (let year = 2005; year <= 2023; year++) {
          const landfill = calculatedEmissionsByYear[year] || 0;
          const wastewater = wastewaterByYear[year];
          const combined = landfill + (wastewater != null && Number.isFinite(wastewater) ? wastewater : 0);
          wasteEmissions.push({ year, category: wasteChartLabel, emissions: Math.round(combined) });
        }
      } catch (error) {
        for (let year = 2005; year <= 2023; year++) {
          wasteEmissions.push({ year, category: 'Waste & wastewater', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        wasteEmissions.push({ year, category: 'Waste & wastewater', emissions: 0 });
      }
    }

    // Fetch Offroad emissions from offroad_emissions table using exact geofips match only.
    // IMPORTANT: Respect the selected geography type:
    // - City selections should use geography_type = 'place'
    // - County selections should use geography_type = 'county'
    // - State selections should use geography_type = 'state'
    const offroadEmissions = [];
    if (geofips) {
      try {
        // Convert geofips to string (no leading zeros - table stores as TEXT without leading zeros)
        const geofipsStr = String(geofips);

        // Determine the selected geography_type from the population table (year 2023 is sufficient)
        // Population uses 'City' while offroad_emissions uses 'place'
        let offroadGeographyType: string | null = null;
        try {
          const { data: popRow, error: popErr } = await supabaseClient
            .from('population')
            .select('geography_type')
            .eq('geofips', geofips)
            .eq('year', 2023)
            .limit(1);
          const popType = popRow?.[0]?.geography_type;
          if (popType) {
            if (popType === 'City') offroadGeographyType = 'place';
            else if (popType === 'County') offroadGeographyType = 'county';
            else if (popType === 'State') offroadGeographyType = 'state';
            else offroadGeographyType = String(popType).toLowerCase();
          }
        } catch (e) {
          // Failed to resolve population geography_type for offroad
        }
        if (!offroadGeographyType) {
          // Fallback heuristic: cities are typically 6 digits, counties 4, state is 1
          const len = geofipsStr.length;
          offroadGeographyType = len >= 6 ? 'place' : len <= 2 ? 'state' : 'county';
        }
        
        // Fetch offroad emissions data for exact geofips only (no county fallback)
        // Note: Table uses geofips (lowercase) and ghg_emissions_tco2e (not emissions)
        // Sum by year since there are multiple categories per year
        const { data: offroadData, error: offroadError } = await supabaseClient
          .from('offroad_emissions')
          .select('year, ghg_emissions_tco2e')
          .eq('geofips', geofipsStr)
          .eq('geography_type', offroadGeographyType)
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });

        // Map emissions by year, summing across all categories for each year
        const offroadEmissionsByYear: Record<number, number> = {};
        (offroadData || []).forEach((r: any) => {
          const year = r.year;
          const emissions = Number(r.ghg_emissions_tco2e || 0);
          // Sum emissions across all categories for the same year
          offroadEmissionsByYear[year] = (offroadEmissionsByYear[year] || 0) + emissions;
        });

        // Create offroad emissions array for all years 2005-2023
        // If no data found, values will be zero
        for (let year = 2005; year <= 2023; year++) {
          const emissions = offroadEmissionsByYear[year] || 0;
          offroadEmissions.push({ year, category: 'Offroad', emissions: Math.round(emissions) });
        }
      } catch (error) {
        for (let year = 2005; year <= 2023; year++) {
          offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
      }
    }

    /** County-only agricultural GHG (ag_emissions); cities get 0 — same as Waste+ Agriculture chart scope */
    const agricultureEmissions: { year: number; category: string; emissions: number }[] = [];
    try {
      if (geofips) {
        const { data: popGeo } = await supabaseClient
          .from('population')
          .select('geography_type')
          .eq('geofips', geofips)
          .limit(1);
        const geoT = popGeo?.[0]?.geography_type;
        const agByYear = await fetchAgricultureEmissionsByYearMt(supabaseClient, String(geofips), geoT);
        for (let year = 2005; year <= 2023; year++) {
          agricultureEmissions.push({
            year,
            category: 'Agriculture',
            emissions: Math.round(agByYear[year] || 0),
          });
        }
      } else {
        for (let year = 2005; year <= 2023; year++) {
          agricultureEmissions.push({ year, category: 'Agriculture', emissions: 0 });
        }
      }
    } catch (e) {
      for (let year = 2005; year <= 2023; year++) {
        agricultureEmissions.push({ year, category: 'Agriculture', emissions: 0 });
      }
    }

    const electricityNonresCategory = ghgElectricityNonresidentialCategoryLabel(geofips);

    // Combine all data
    const allEmissions = [
      ...lightDutyEmissions.map(d => ({ ...d, category: 'Light Duty Vehicles' })),
      ...heavyDutyEmissions.map(d => ({ ...d, category: 'Heavy Duty Vehicles' })),
      ...naturalGasEmissions.map(d => ({ ...d, category: 'Natural Gas Residential' })),
      ...electricityCommercialEmissions.map(d => ({ ...d, category: electricityNonresCategory })),
      ...electricityResidentialEmissions.map(d => ({ ...d, category: 'Electricity Residential' })),
      ...naturalGasCommercialEmissions.map(d => ({ ...d, category: 'Natural Gas Commercial' })),
      ...wasteEmissions,
      ...offroadEmissions,
      ...agricultureEmissions,
    ];

    // Get unique years and categories
    const years = Array.from(new Set(allEmissions.map(d => d.year))).sort((a, b) => a - b);
    const categories = Array.from(new Set(allEmissions.map(d => d.category)));

    // Create datasets for each category
    const datasets = categories.map((category, index) => {
      // Color mapping based on category name - using shades of same color for related sources
      const getColorForCategory = (cat: string): { bg: string, border: string } => {
        switch (cat) {
          case 'Light Duty Vehicles':
            return { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' };  // Transportation - darker blue
          case 'Heavy Duty Vehicles':
            return { bg: 'rgba(96, 165, 250, 0.8)', border: 'rgba(96, 165, 250, 1)' };  // Transportation - lighter blue
          case 'Natural Gas Residential':
          case 'Natural Gas Commercial':
          case 'Electricity Residential':
          case 'Electricity Commercial':
          case 'Electricity Nonresidential':
            return ghgInventoryCategoryRgba(cat);
          case 'Waste':
          case 'Waste & wastewater':
            return { bg: 'rgba(236, 72, 153, 0.8)', border: 'rgba(236, 72, 153, 1)' };  // Pink for waste
          case 'Offroad':
            return { bg: 'rgba(255, 210, 0, 0.8)', border: 'rgba(255, 210, 0, 1)' };  // UC Gold for offroad
          case 'Agriculture':
            return { bg: 'rgba(22, 101, 52, 0.85)', border: 'rgba(22, 101, 52, 1)' }; // County ag (ag_emissions)
          default:
            return { bg: 'rgba(100, 116, 139, 0.8)', border: 'rgba(100, 116, 139, 1)' };
        }
      };
      
      const colors = getColorForCategory(category);
      const color = colors.bg;
      const borderColor = colors.border;

      const data = years.map(year => {
        const entry = allEmissions.find(d => d.category === category && d.year === year);
        const value = entry ? entry.emissions : 0;
        return value;
      });

      return {
        label: category,
        data,
        backgroundColor: color,
        borderColor,
        borderWidth: 2
      };
    });

    const chartData = {
      labels: years,
      datasets
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: `GHG Inventory - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Emissions (Metric Tons CO₂e)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };

    // Calculate metrics
    const latestYear = years[years.length - 1];
    const latestYearData = allEmissions.filter(d => d.year === latestYear);
    const totalEmissions = latestYearData.reduce((sum, d) => sum + d.emissions, 0);
    const lightDutyLatest = latestYearData.find(d => d.category === 'Light Duty Vehicles')?.emissions || 0;
    const heavyDutyLatest = latestYearData.find(d => d.category === 'Heavy Duty Vehicles')?.emissions || 0;
    const naturalGasLatest = latestYearData.find(d => d.category === 'Natural Gas Residential')?.emissions || 0;

    const headlines = computeSectorInventoryHeadlines(allEmissions);

    const evidenceYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
    const ldvVmtSeries = emptyYearSeries(evidenceYears, 'miles');
    vmtData.forEach((d) => {
      const idx = evidenceYears.indexOf(d.year);
      if (idx < 0) return;
      const v = Number(d.vmt);
      ldvVmtSeries.values[idx] = Number.isFinite(v) && v > 0 ? v : v === 0 ? 0 : null;
      if (ldvVmtSeries.sources) ldvVmtSeries.sources[idx] = d.source ?? d.method_note ?? null;
      if (ldvVmtSeries.statuses) ldvVmtSeries.statuses[idx] = d.source ?? 'modeled';
    });
    const hdvVmtSeries = emptyYearSeries(evidenceYears, 'miles');
    evidenceYears.forEach((y, i) => {
      const v = hdvVmtByYear.get(y);
      hdvVmtSeries.values[i] = v == null || !Number.isFinite(v) ? null : v;
      if (hdvVmtSeries.statuses) hdvVmtSeries.statuses[i] = v == null ? null : 'modeled';
      if (hdvVmtSeries.sources) hdvVmtSeries.sources[i] = v == null ? null : 'emfac2025_fleet.total_vmt';
    });
    const wasteTonsSeries = emptyYearSeries(evidenceYears, 'tons');
    const wasteFactorSeries = emptyYearSeries(evidenceYears, 'MT CO2e/ton');
    evidenceYears.forEach((y, i) => {
      const tons = wasteTonsByYearForLogging[y];
      wasteTonsSeries.values[i] = tons == null || !Number.isFinite(tons) ? null : tons;
      if (wasteTonsSeries.statuses) wasteTonsSeries.statuses[i] = tons == null ? null : 'modeled';
      const f = wasteEmissionFactorsByYearForLogging[y];
      wasteFactorSeries.values[i] = f == null || !Number.isFinite(f) ? null : f;
    });
    const ciSeries = emptyYearSeries(evidenceYears, 'g CO2e/kWh');
    evidenceYears.forEach((y, i) => {
      const g = tableCiGPerKwhByYear.get(y);
      ciSeries.values[i] = g == null || !Number.isFinite(g) || g <= 0 ? null : g;
      if (ciSeries.statuses) ciSeries.statuses[i] = ciSeries.values[i] == null ? null : 'modeled';
    });

    const inventoryEvidence: GhgInventoryEvidence = {
      years: evidenceYears,
      ldvVmt: ldvVmtSeries,
      hdvVmt: hdvVmtSeries,
      elecResKwh: energyActivitySeries(evidenceYears, buildingsEnergyData.electricity.residential, 'kWh'),
      elecComKwh: energyActivitySeries(evidenceYears, buildingsEnergyData.electricity.commercial, 'kWh'),
      ngResTherms: energyActivitySeries(evidenceYears, buildingsEnergyData.naturalGas.residential, 'therms'),
      ngComTherms: energyActivitySeries(evidenceYears, buildingsEnergyData.naturalGas.commercial, 'therms'),
      wasteTons: wasteTonsSeries,
      wasteEmissionFactorMtPerTon: wasteFactorSeries,
      electricityCarbonIntensityGPerKwh: ciSeries,
      ngEmissionFactorMtPerTherm: 0.00529,
      ngEmissionFactorNote: 'Copied from the GHG calculation (EPA GHG Equivalencies: 0.00529 MT CO2e/therm). Not recomputed in the report.',
      replicaMethodVersion: COMBINED_2015_2022_METHOD_VERSION,
      replicaAttribution:
        'LDV VMT is the ClimatePlans combined residential-activity series used by fetchGHGInventoryData (PRIVATE_AUTO + ON_DEMAND_AUTO from Replica, with documented Combined backcast). This is estimated travel demand, not observed traffic counts.',
      hdvClass: 'HDV',
      electricityProviderByYear: evidenceYears.map((y) => {
        const idx = buildingsEnergyData.electricity.residential.years.indexOf(y);
        return idx >= 0 ? buildingsEnergyData.electricity.residential.providers[idx] ?? null : null;
      }),
      hdvSmoothingApplied: evidenceYears.map((y) => (y < 2015 ? null : hdvSmoothingAppliedByYear.get(y) ?? null)),
      hdvActivityRole: evidenceYears.map((y, i) => {
        const v = hdvVmtSeries.values[i];
        if (v == null) return null;
        if (y < 2015) return 'contextual';
        if (hdvSmoothingAppliedByYear.get(y)) return 'contextual';
        return 'calculation_input';
      }),
    };

      return {
        chartData,
        chartOptions,
        latestYear,
        totalEmissions,
        lightDutyEmissions: lightDutyLatest,
        heavyDutyEmissions: heavyDutyLatest,
        naturalGasEmissions: naturalGasLatest,
        totalEmissions2005: headlines.totalEmissions2005,
        totalEmissions2023: headlines.totalEmissions2023,
        percentChange: headlines.percentChange2023Vs2005,
        largestSectorSharePercent2023: headlines.largestSectorSharePercent2023,
        largestSectorName2023: headlines.largestSectorName2023,
        inventoryEvidence,
      };
    } catch (error) {
      console.error('Error fetching GHG inventory data:', error);
      throw error;
    } finally {
      // Remove the request from the map when done (success or failure)
      inFlightRequests.delete(location);
    }
  })();
  
  // Store the promise in the map
  inFlightRequests.set(location, requestPromise);
  
  return requestPromise;
}
